import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Mail } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { User } from "../types";
import { employeeBroadcastService } from "@/services/employeeBroadcastService";

interface EmailEmployeesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Exactly the employees the current filters matched. */
  recipients: User[];
}

/**
 * The API rejects a bad request with {error: "..."}, which is the sentence
 * worth showing. Axios only offers "Request failed with status code 400".
 */
const messageFor = (error: unknown): string => {
  const serverMessage = (error as { response?: { data?: { error?: string } } })
    ?.response?.data?.error;
  if (serverMessage) return serverMessage;
  return error instanceof Error ? error.message : "Failed to send the message";
};

/** A datetime-local value is local wall time; the API wants an instant. */
const toIso = (value: string): string | null =>
  value ? new Date(value).toISOString() : null;

export const EmailEmployeesDialog: React.FC<EmailEmployeesDialogProps> = ({
  isOpen,
  onClose,
  recipients,
}) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sendAt, setSendAt] = useState("");
  const [sending, setSending] = useState(false);

  // A closed dialog should not hold onto last time's draft.
  useEffect(() => {
    if (!isOpen) {
      setSubject("");
      setBody("");
      setSendAt("");
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast({
        title: "Add a subject and a message",
        variant: "destructive",
      });
      return;
    }

    setSending(true);
    try {
      const result = await employeeBroadcastService.send({
        userIds: recipients.map((r) => r.id),
        subject: subject.trim(),
        body: body.trim(),
        sendAt: toIso(sendAt),
      });

      toast({
        title: sendAt
          ? `Scheduled for ${result.scheduled} employees`
          : `Sending to ${result.scheduled} employees`,
        // Naming the unreachable matters: those are the ones to phone instead.
        description: result.skippedNames.length
          ? `${result.skippedNames.join(", ")} take no notifications and were skipped.`
          : undefined,
        variant: "success",
      });
      onClose();
    } catch (error) {
      toast({ title: messageFor(error), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Email these employees</DialogTitle>
          <DialogDescription>
            Goes to the {recipients.length} employee
            {recipients.length === 1 ? "" : "s"} your filters matched, by each
            one&apos;s own notification preferences.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="broadcast-subject">Subject</Label>
            <Input
              id="broadcast-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Craps opening Saturday"
              disabled={sending}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="broadcast-body">Message</Label>
            <Textarea
              id="broadcast-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="We need one more dealer for Saturday night…"
              disabled={sending}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="broadcast-send-at">Send at</Label>
            <Input
              id="broadcast-send-at"
              type="datetime-local"
              value={sendAt}
              onChange={(e) => setSendAt(e.target.value)}
              disabled={sending}
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to send now.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || recipients.length === 0}
          >
            {sending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-2 h-4 w-4" />
            )}
            {sendAt ? "Schedule" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
